import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getActivePlayers } from '../api/client';
import { SiteFilter, siteParam, siteName, useSites } from '../components/SiteFilter';
import { ExportButtons } from '../components/ExportButtons';
import { formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { Th } from '../components/Th';
import { PlayerLeaderboard } from '../components/PlayerLeaderboard';

interface PlayerRow {
  date: string;
  site_id: number;
  active_players: number;
  new_players: number;
}

export const ActivePlayers: React.FC = () => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [data, setData] = useState<PlayerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [siteIds, setSiteIds] = useState<number[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getActivePlayers({ date_from: dateFrom, date_to: dateTo, site_ids: siteParam(allSites, siteIds) });
      setData(response.data || []);
    } catch (err) {
      console.error('Failed to load active players:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateFrom, dateTo, siteIds]);

  const latestRow = data.length > 0 ? data[data.length - 1] : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('activePlayersReport.title')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('activePlayersReport.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <SiteFilter selected={siteIds} onChange={setSiteIds} />
        <button onClick={fetchData} disabled={loading}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-50">
          {loading ? t('filters.loading') : t('filters.apply')}
        </button>
        <ExportButtons reportType="active_players" dateFrom={dateFrom} dateTo={dateTo} siteIds={siteParam(allSites, siteIds)} />
      </div>

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('activePlayersReport.activePlayersLatest')}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(latestRow?.active_players || 0)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('activePlayersReport.newPlayersLatest')}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(latestRow?.new_players || 0)}</p>
        </div>
      </div>

      {/* Player performance overview — top / bottom performers */}
      <PlayerLeaderboard siteIds={siteIds} dateFrom={dateFrom} dateTo={dateTo} />

      {/* Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('activePlayersReport.playerActivityTrend')}</h2>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }} />
            <Legend />
            <Line type="monotone" dataKey="active_players" name="Active Players" stroke="#009EA0" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="new_players" name="New Players" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[
                { l: t('activePlayersReport.columnDate'), i: 'date' },
                { l: t('activePlayersReport.columnSite'), i: 'site' },
                { l: t('activePlayersReport.columnActivePlayers'), i: 'activePlayers' },
                { l: t('activePlayersReport.columnNewPlayers'), i: 'newPlayers' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {data.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">{t('activePlayersReport.noDataAvailable')}</td></tr>
            ) : data.map((row, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{row.date || ''}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white" title={`ID ${row.site_id}`}>{siteName(allSites, row.site_id)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.active_players || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.new_players || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
